import { useState, useEffect } from "react";
import axios from "axios";
import { showData } from "../../Utils/APIs/commonShowAPI";

const SpList = () => {
  const headingList = [
    "Saral-Id",
    "Installer",
    "Amount",
    "RequestBy",
    "Requested Date",
  ];

  // Keys must match the fields in your API response
  const key = ["saralId", "installerId", "amount", "createdBy", "createdAt"];

  const [spList, setSpList] = useState([]);

  // Holds the ids of selected rows
  const [selectedIds, setSelectedIds] = useState([]);

  /**
   * Resolve a row's id in one place (works with _id or id)
   */
  const getRowId = (item) => item?._id ?? item?.id;

  /**
   * Fetch SP request list
   */
  const fetchList = async () => {
    try {
      await showData("installer/selectionPaymentRequest", setSpList);
    } catch (error) {
      console.error("Failed to fetch SP list:", error);
    }
  };

  /**
   * Approve / Reject selected IDs
   */
  const selectedIdHandler = async (data) => {
    try {
      const sendRequest = await axios.put(
        `${process.env.REACT_APP_API_URL}/installer/selectionPaymentRequest`,
        data
      );

      const response = sendRequest.data;

      if (response?.success) {
        await showData("installer/selectionPaymentRequest", setSpList);

        // Clear selected rows after successful update
        setSelectedIds([]);

        alert(response.message);
      }
    } catch (error) {
      console.error("Selection payment request error:", error);

      const message =
        error?.response?.data?.message ||
        error?.response?.message ||
        error?.message ||
        "Something went wrong";

      alert(message);
    }
  };

  /**
   * Fetch data on component mount
   */
  useEffect(() => {
    fetchList();
  }, []);

  /**
   * Keep selection in sync with the list:
   * drop any selected ids that no longer exist in spList
   */
  useEffect(() => {
    const validIds = new Set(spList.map(getRowId).filter(Boolean));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [spList]);

  /**
   * Only ids that actually exist
   */
  const allIds = spList.map(getRowId).filter(Boolean);

  /**
   * All rows selected? Compare by content, not by length.
   */
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  /**
   * At least one row selected?
   */
  const hasSelection = selectedIds.length > 0;

  /**
   * Toggle individual row
   */
  const toggleRow = (id) => {
    if (!id) return; // ignore rows without an id
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /**
   * Select / deselect all rows
   */
  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : allIds);
  };

  /**
   * Approve selected rows
   */
  const handleApprove = () => {
    selectedIdHandler({ ids: selectedIds, status: true });
  };

  /**
   * Reject selected rows
   */
  const handleReject = () => {
    selectedIdHandler({ ids: selectedIds, status: false });
  };

  /**
   * Format table cell values.
   * installerId and createdBy can be populated objects,
   * and React cannot render an object directly.
   */
  const formatCell = (field, value) => {
    // Empty value
    if (value === undefined || value === null || value === "") {
      return "N/A";
    }

    // Format date
    if (field === "createdAt") {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "N/A";
      }

      return date.toISOString().split("T")[0];
    }

    // Populated objects (installerId, createdBy, or anything else)
    if (typeof value === "object") {
      return value?.name || value?.username || value?._id || "N/A";
    }

    return value;
  };

  const cellClass = "px-3 py-2 whitespace-nowrap text-sm text-center";

  return (
    <div className="w-full h-full flex flex-col">
      {/* Heading */}
      <div className="mainHeading">
        <h1 className="text-3xl font-semibold tracking-widest text-center p-2 bg-gray-50 rounded-lg">
          S<span className="text-lg">P-REQUEST</span> L
          <span className="text-lg">IST</span>
        </h1>
      </div>

      {/* Approve / Reject Buttons */}
      <div className="flex items-center justify-end gap-3 px-2 py-3">
        <span className="text-sm text-gray-600 mr-auto">
          {selectedIds.length} selected
        </span>

        {/* Approve */}
        <button
          type="button"
          onClick={handleApprove}
          disabled={!hasSelection}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
        >
          Approve
        </button>

        {/* Reject */}
        <button
          type="button"
          onClick={handleReject}
          disabled={!hasSelection}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
        >
          Reject
        </button>
      </div>

      {/* Table Container */}
      <div className="flex-1 min-h-0 w-full overflow-auto">
        <table className="w-full table-auto border-collapse">
          {/* Table Header */}
          <thead className="sticky top-0 z-20 text-xs bg-gray-800 text-gray-100 uppercase">
            <tr>
              {/* Select All */}
              <th scope="col" className="px-3 py-3 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={allIds.length === 0}
                  aria-label="Select all rows"
                />
              </th>

              {/* Serial Number */}
              <th scope="col" className="px-3 py-3 text-center font-semibold">
                #
              </th>

              {/* Dynamic Headers */}
              {headingList.map((item, index) => (
                <th
                  key={index}
                  scope="col"
                  className="px-3 py-3 text-center font-semibold"
                >
                  {item}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {spList.length > 0 ? (
              spList.map((dataItem, index) => {
                const rowId = getRowId(dataItem);
                const isSelected = rowId ? selectedIds.includes(rowId) : false;

                return (
                  <tr
                    key={rowId || index}
                    className={`border-b border-gray-200 ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                  >
                    {/* Checkbox */}
                    <td className={cellClass}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(rowId)}
                        disabled={!rowId}
                        aria-label={`Select row ${index + 1}`}
                      />
                    </td>

                    {/* Serial Number */}
                    <td className={cellClass}>{index + 1}</td>

                    {/* Dynamic Cells */}
                    {key.map((field) => (
                      <td key={field} className={cellClass}>
                        {formatCell(field, dataItem?.[field])}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={key.length + 2} className="text-center py-4">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SpList;
// MT4400201048237